'use client';

import { GetAvailableVariables, VariableSlug } from '@/api/generated/model';
import { ChartDataset, EAggregatedDataType } from '@/api/requests/types/charts.type';
import { MULTIPLE_COLORS, MULTIPLE_COLORS_AMOUNT } from '@/constants/ui.constants';
import { sentimentsEmotionsHexMap } from '@/domains/home/components/home-tabs/user-intelligence/_components';
import {
  buildUnitSuffix,
  getTimeUnitLabel,
  resolveUnitForCustomVariable,
} from '@/domains/chart/components/create-chart/charts/analytics/custom-variables.chart.utils';
import { capitalizeFirstLetter } from '@/utils/capitalize-first-letter';
import {
  formatCurrency as formatCurrencyValue,
  formatDecimal,
  formatDuration,
  FORMAT_PRESETS,
  formatNumber,
  formatPercentage,
} from '@/utils/format.util';
import { CURRENCY_VARIABLE_SLUGS } from '@/utils/currency.util';
import { formatDateHuman } from '@/utils/date.util';
import { translate } from '@/utils/translations';
import { Chart as ChartJS, ScriptableContext, ChartType } from 'chart.js';
import { capitalize } from 'lodash';
import pluralize from 'pluralize';

export const chartTrimmedLabel = (label: string) => {
  return label.length > 20 ? label.slice(0, 20) + '...' : label;
};

export function formatAxisValue(value: number, max: number, prepend?: string): string {
  let formatted: string;

  if (max < 1) {
    formatted = value.toFixed(2);
  } else if (max < 10) {
    formatted = value.toFixed(1);
  } else {
    formatted = formatNumber(value, FORMAT_PRESETS.axis);
  }

  if (!prepend) return formatted;
  if (formatted.startsWith('-')) return `-${prepend}${formatted.slice(1)}`;
  return `${prepend}${formatted}`;
}

const getChartColors = (labels: string[]) => {
  const colors = labels.map((label, index) => MULTIPLE_COLORS[index % (MULTIPLE_COLORS_AMOUNT - 1)] || '');

  const alphaColors = colors.map((color) => color + '80');

  return { colors, alphaColors };
};

// NOTE: For horizontalBar charts
export const prepareDatasetsHorizontalBar = ({
  datasets,
  labels,
  capitalized,
  isSentimentBarChart,
  colors,
}: {
  capitalized?: boolean;
  datasets: ChartDataset[];
  labels: string[];
  isSentimentBarChart?: boolean;
  colors?: string[];
}) => {
  const finalColors = colors ?? labels.map((_, index) => MULTIPLE_COLORS[index % MULTIPLE_COLORS_AMOUNT] || '');
  const sentimentColors = labels.map((label) => sentimentsEmotionsHexMap(translate('sentiment', label), 'sentiment'));
  const getSentimentBGColorGradient = (context: ScriptableContext<'bar'>) => {
    const ctx = context.chart.ctx;
    // if (!ctx) return;

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const { chart, datasetIndex, index } = context;
    const ds = chart.data.datasets[datasetIndex];
    const value = ds.data[index];
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const x = chart.scales.x.getPixelForValue(value);
    const meta = chart.getDatasetMeta(datasetIndex);
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const data = meta.data[index] as unknown as { width: number | null; base: number | null };
    const { width, base } = data || {};

    const gradients = labels.map((label) => {
      const gradient = ctx?.createLinearGradient(base || 0, width || 0, x || 0, width || 0);
      gradient.addColorStop(
        0,
        (sentimentsEmotionsHexMap(translate('sentiment', label), 'sentiment') ?? finalColors[0]) + '05',
      );
      gradient.addColorStop(
        0.5,
        (sentimentsEmotionsHexMap(translate('sentiment', label), 'sentiment') ?? finalColors[0]) + '50',
      );
      gradient.addColorStop(
        1,
        (sentimentsEmotionsHexMap(translate('sentiment', label), 'sentiment') ?? finalColors[0]) + '99',
      );
      return gradient;
    });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return gradients as any;
  };

  const getBGColorGradient = (context: ScriptableContext<'bar'>) => {
    const ctx = context.chart.ctx;
    // if (!ctx) return;

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const { chart, datasetIndex, index } = context;
    const ds = chart.data.datasets[datasetIndex];
    const value = ds.data[index];
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const x = chart.scales.x.getPixelForValue(value);
    const meta = chart.getDatasetMeta(datasetIndex);
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const data = meta.data[index] as unknown as { width: number | null; base: number | null };
    const { width, base } = data || {};

    const gradients = labels.map((label, index) => {
      const gradient = ctx?.createLinearGradient(base || 0, width || 0, x || 0, width || 0);
      const color = finalColors[index % finalColors.length];
      gradient.addColorStop(0, `${color}05`);
      gradient.addColorStop(0.5, `${color}50`);
      gradient.addColorStop(1, `${color}99`);
      return gradient;
    });

    return gradients;
  };

  if (isSentimentBarChart) {
    datasets = datasets.map((dataset) => {
      return {
        ...dataset,
        label: capitalize(translate('sentiment', dataset.label)),
      };
    });
  }

  return datasets.map((dataset) => ({
    label: capitalized ? capitalizeFirstLetter(dataset.label) : dataset.label,
    data: dataset.data.map((item) => {
      const value = typeof item === 'number' ? item : item?.y;
      return value || 0;
    }),
    borderColor: isSentimentBarChart ? sentimentColors : finalColors,
    backgroundColor: isSentimentBarChart ? getSentimentBGColorGradient : getBGColorGradient,
    hoverBackgroundColor: isSentimentBarChart ? getSentimentBGColorGradient : getBGColorGradient,
    borderWidth: 1,
    borderRadius: 3,
    barThickness: 17,
    barPercentage: 0.5, // Adjust the bar width (0.1 to 1 for 10% to 100% of the available space)
    categoryPercentage: 1, // Adjust the space between bars (0.1 to 1 for 10% to 100% of the bar width)
    minBarLength: 1,
  }));
};

export const prepareDatasetsDoughnut = ({
  datasets,
  labels,
  capitalized,
  isSentimentBarChart,
  colors,
}: {
  capitalized?: boolean;
  datasets: ChartDataset[];
  labels: string[];
  isSentimentBarChart?: boolean;
  colors?: string[];
}) => {
  const finalColors = colors ?? labels.map((_, index) => MULTIPLE_COLORS[index % MULTIPLE_COLORS_AMOUNT] || '');
  const sentimentColors = labels.map((label) => sentimentsEmotionsHexMap(translate('sentiment', label), 'sentiment'));
  const getSentimentBGColor = () => {
    const colors = labels.map((label) => {
      return (sentimentsEmotionsHexMap(translate('sentiment', label), 'sentiment') ?? finalColors[0]) + '10';
    });
    return colors;
  };
  const getSentimentHoverColor = () => {
    const colors = labels.map((label) => {
      return (sentimentsEmotionsHexMap(translate('sentiment', label), 'sentiment') ?? finalColors[0]) + '50';
    });
    return colors;
  };

  const getBGColor = () => {
    const colors = labels.map((_, index) => {
      return finalColors[index % finalColors.length] + '10';
    });

    return colors;
  };

  const getHoverColor = () => {
    const colors = labels.map((_, index) => {
      return finalColors[index % finalColors.length] + '50';
    });

    return colors;
  };

  if (isSentimentBarChart) {
    datasets = datasets.map((dataset) => {
      return {
        ...dataset,
        label: capitalize(translate('sentiment', dataset.label)),
      };
    });
  }

  return datasets.map((dataset) => ({
    label: capitalized ? capitalizeFirstLetter(dataset.label) : dataset.label,
    data: dataset.data.map((item) => {
      const value = typeof item === 'number' ? item : item?.y;
      return value || 0;
    }),
    borderColor: isSentimentBarChart ? sentimentColors : finalColors,
    backgroundColor: isSentimentBarChart ? getSentimentBGColor() : getBGColor(),
    hoverBackgroundColor: isSentimentBarChart ? getSentimentHoverColor() : getHoverColor(),
    borderWidth: 1,
    borderRadius: 3,
    barThickness: 17,
    barPercentage: 0.5, // Adjust the bar width (0.1 to 1 for 10% to 100% of the available space)
    categoryPercentage: 1, // Adjust the space between bars (0.1 to 1 for 10% to 100% of the bar width)
    minBarLength: 1,
  }));
};

export const prepareDatasetsVerticalBar = ({
  datasets,
  labels,
  normalize,
}: {
  capitalized?: boolean;
  datasets: ChartDataset[];
  labels: string[];
  isSentimentBarChart?: boolean;
  normalize?: boolean;
}) => {
  const colors = labels.map((_, index) => MULTIPLE_COLORS[index % MULTIPLE_COLORS_AMOUNT] || '');
  // const getBGColorGradient = (context: ScriptableContext<'bar'>) => {
  //   const ctx = context.chart.ctx;
  //   // if (!ctx) return;

  //   // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  //   // @ts-ignore
  //   const { chart, datasetIndex, index } = context;
  //   const ds = chart.data.datasets[datasetIndex];
  //   const value = ds.data[index];
  //   // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  //   // @ts-ignore
  //   const x = chart.scales.x.getPixelForValue(value);
  //   const meta = chart.getDatasetMeta(datasetIndex);
  //   // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  //   // @ts-ignore
  //   const data = meta.data[index] as unknown as { width: number | null; base: number | null };
  //   const { width, base } = data || {};

  //   const gradients = labels.map((label, index) => {
  //     const gradient = ctx?.createLinearGradient(base || 0, width || 0, x || 0, width || 0);
  //     gradient.addColorStop(0, `${colors[index]}05`);
  //     gradient.addColorStop(0.5, `${colors[index]}50`);
  //     gradient.addColorStop(1, `${colors[index]}99`);
  //     return gradient;
  //   });

  //   return gradients;
  // };
  const flattenedData = datasets.flatMap((dataset) =>
    dataset.data.map((item) => {
      const value = typeof item === 'number' ? item : item?.y;
      return value || 0;
    }),
  );

  const total = flattenedData.reduce((acc, curr) => acc + curr, 0);
  const percentage = flattenedData.map((item) => (item / total) * 100);

  return [
    {
      label: 'label',
      data: normalize ? percentage : flattenedData,
      borderColor: colors,
      backgroundColor: colors,
      // hoverBackgroundColor: getBGColorGradient,
      borderWidth: 1,
      borderRadius: 3,
      barThickness: 17,
      barPercentage: 0.5,
      categoryPercentage: 1,
      minBarLength: 1,
    },
  ];
};

// NOTE: For pie, doughnut, polarArea charts
export const prepareDatasetsCircle = ({ datasets, labels }: { datasets: ChartDataset[]; labels: string[] }) => {
  const { colors, alphaColors } = getChartColors(labels);

  return datasets.map((dataset) => {
    return {
      label: formatDateHuman(new Date(dataset.label)),
      data: dataset.data.map((item) => item || 0),
      borderColor: colors,
      hoverBorderColor: colors,
      backgroundColor: alphaColors,
      hoverBackgroundColor: alphaColors,
    };
  });
};

type ChartLabelFormatOptions = {
  compactNumber?: boolean;
  compactWithDecimals?: boolean;
  fixedDecimals?: number;
};

const getDefaultNumericLabel = (value: number, options: ChartLabelFormatOptions) => {
  const { compactNumber, compactWithDecimals, fixedDecimals } = options;

  if (compactWithDecimals) return formatNumber(value, FORMAT_PRESETS.chartMetric);
  if (fixedDecimals !== undefined) return value.toFixed(fixedDecimals);
  if (compactNumber) return formatNumber(value, FORMAT_PRESETS.chartMetric);
  return formatNumber(value, FORMAT_PRESETS.plain);
};

const formatPercentageLabel = (num: number, options?: ChartLabelFormatOptions) => {
  if (options?.compactWithDecimals || options?.fixedDecimals !== undefined) {
    const decimals = options.compactWithDecimals ? 2 : options.fixedDecimals!;
    return formatPercentage(num, { inputScale: 'percent', decimals });
  }

  return formatPercentage(num, { inputScale: 'percent' });
};

const formatFloatLabel = (num: number, suffix: string, options?: ChartLabelFormatOptions) => {
  if (options?.compactWithDecimals || options?.fixedDecimals !== undefined) {
    const decimals = options.compactWithDecimals ? 2 : options.fixedDecimals!;
    return formatDecimal(num, { decimals, suffix });
  }

  return formatDecimal(num, { suffix });
};

const CURRENCY_VARIABLES = CURRENCY_VARIABLE_SLUGS;

const HOURS_VARIABLES = new Set<string>([
  'hours_saved',
  VariableSlug.roi_hours_saved,
  VariableSlug.roi_hours_saved_per_user,
  VariableSlug.roi_hours_saved_per_interaction,
]);

const RATE_VARIABLES = new Set<string>([
  EAggregatedDataType.ERROR_RATE,
  EAggregatedDataType.ERROR_RATE_EXPLICIT,
  EAggregatedDataType.RETURN_RATE,
  EAggregatedDataType.BOUNCE_RATE,
  EAggregatedDataType.SATISFACTION,
  EAggregatedDataType.FRUSTRATION,
  EAggregatedDataType.PENETRATION,
  VariableSlug.roi_success_rate,
]);

export const formatChartLabel = ({
  label,
  variable,
  skipFormat,
  detailedView = false,
  compactNumber = true,
  compactWithDecimals = false,
  fixedDecimals,
  availableVariables,
}: {
  label: string;
  variable: string | GetAvailableVariables | undefined;
  detailedView?: boolean;
  compactNumber?: boolean;
  compactWithDecimals?: boolean;
  fixedDecimals?: number;
  skipFormat?: string[];
  availableVariables?: GetAvailableVariables[];
}) => {
  const value = Number(label);
  const effectiveCompactNumber = detailedView ? false : compactNumber;
  const formatOptions: ChartLabelFormatOptions = {
    compactNumber: effectiveCompactNumber,
    compactWithDecimals,
    fixedDecimals,
  };
  const defaultNumericLabel = getDefaultNumericLabel(value, formatOptions);

  const variableSlug = typeof variable === 'string' ? variable : variable?.slug;
  const availableVariablesBySlug = availableVariables
    ? new Map(availableVariables.map((entry) => [entry.slug, entry]))
    : null;
  const variableMeta = typeof variable === 'string' ? availableVariablesBySlug?.get(variable) : variable;
  if (variableSlug && skipFormat?.includes(variableSlug)) return defaultNumericLabel;

  if (variableMeta?.is_custom && variableMeta.raw_formula && availableVariablesBySlug) {
    if (Number.isNaN(value)) return defaultNumericLabel;

    const unitResolution = resolveUnitForCustomVariable(variableMeta, availableVariablesBySlug);
    if (unitResolution.status === 'invalid') return defaultNumericLabel;
    if (!unitResolution.unit) return defaultNumericLabel;

    const unitKeys = Object.keys(unitResolution.unit);
    const hasOnlyTimeAndUsers = unitKeys.every((key) => key === 'time' || key === 'users');

    if (!hasOnlyTimeAndUsers) return defaultNumericLabel;

    const timeExp = unitResolution.unit.time ?? 0;
    const usersExp = unitResolution.unit.users ?? 0;

    const timeLabel = getTimeUnitLabel(value);
    if (!timeLabel && timeExp !== 0) return defaultNumericLabel;

    if (timeExp === 1) {
      const baseValue = formatDuration(value, { inputUnit: 'seconds', style: 'human' });
      if (usersExp === 0) return baseValue;
      const absUsers = Math.abs(usersExp);
      const usersToken = absUsers === 1 ? 'users' : `users^${absUsers}`;
      return usersExp > 0 ? `${baseValue}*${usersToken}` : `${baseValue}/${usersToken}`;
    }

    const suffix = buildUnitSuffix(unitResolution.unit, { timeLabel, includeTime: true });
    return suffix ? `${defaultNumericLabel} ${suffix}` : defaultNumericLabel;
  }

  if (variableSlug === EAggregatedDataType.CONVERSATION_TIME) {
    return formatDuration(value, { inputUnit: 'seconds', style: 'human' });
  }

  if (variableSlug && CURRENCY_VARIABLES.has(variableSlug)) {
    if (detailedView) {
      const decimals = compactWithDecimals ? 2 : (fixedDecimals ?? 2);
      return formatCurrencyValue(value, { style: 'localized', decimals });
    }

    return formatNumber(value, FORMAT_PRESETS.chartMetric);
  }

  if (variableSlug && HOURS_VARIABLES.has(variableSlug)) {
    return formatNumber(value, FORMAT_PRESETS.chartMetric);
  }

  if (variableSlug === EAggregatedDataType.LATENCY) {
    return formatFloatLabel(Number(label), 's', formatOptions);
  }

  if (variableSlug === EAggregatedDataType.PERCENTAGE) {
    return formatPercentageLabel(Number(label), formatOptions);
  }

  if (variableSlug === EAggregatedDataType.TIMES) {
    const numValue = Number(label);
    const keyword = numValue === 1 ? 'time' : 'times';
    return `${getDefaultNumericLabel(numValue, formatOptions)} ${keyword}`;
  }

  if (variableSlug === EAggregatedDataType.INTERACTIONS) {
    const numValue = Number(label ?? 0);
    return `${getDefaultNumericLabel(numValue, formatOptions)} ${pluralize('interaction', numValue)}`;
  }

  if (variableSlug && RATE_VARIABLES.has(variableSlug)) {
    if (Number.isNaN(value)) return 'NA';
    return formatPercentageLabel(value * 100, formatOptions);
  }

  return defaultNumericLabel;
};

export function breakTooltipLabelAtWordNumber(label: string, wordNumber: number) {
  const words = label.split(' ');

  const lines = [];
  for (let i = 0; i < words.length; i += wordNumber) {
    lines.push(words.slice(i, i + wordNumber).join(' '));
  }

  return lines;
}

export function updateChart<TChartType extends ChartType>(chart: ChartJS<TChartType>) {
  try {
    chart.update();
  } catch {
    // do nothing
  }
}

export function showChartTooltip<TChartType extends ChartType>(chart: ChartJS<TChartType>, show: boolean) {
  const tooltip = chart.options?.plugins?.tooltip;
  if (tooltip) tooltip.enabled = show;
}

interface InputDataset {
  label: string;
  data: { y: number | undefined }[];
}
export const prepareDatasetsClusteredBar = ({
  datasets,
  labels,
  capitalized = false,
  colors,
}: {
  datasets: InputDataset[];
  labels: string[];
  capitalized?: boolean;
  colors?: string[];
}) => {
  return labels.map((variableName, variableIndex) => {
    const color = colors
      ? colors[variableIndex % colors.length]
      : MULTIPLE_COLORS[variableIndex % MULTIPLE_COLORS_AMOUNT];

    const dataValues = datasets.map((dataset) => {
      return dataset.data?.[variableIndex]?.y ?? 0;
    });

    let formattedLabel = variableName || '';
    if (capitalized) {
      formattedLabel = capitalize(formattedLabel);
    }

    return {
      label: formattedLabel,
      data: dataValues,
      backgroundColor: color,
      borderColor: color,
      borderWidth: 1,
      borderRadius: 4,
      borderSkipped: false,
      barThickness: 'flex' as const,
      maxBarThickness: 60,
      categoryPercentage: 0.8,
      barPercentage: 0.9,
    };
  });
};

export const getGroupNamesFromDatasets = (
  datasets: InputDataset[],
  breakdown?: string,
  capitalized?: boolean,
): string[] => {
  return datasets.map((dataset) => {
    let formattedLabel = dataset.label || '';

    if (breakdown && dataset.label) {
      const translationKey = breakdown as keyof typeof translate;
      formattedLabel = translate(translationKey, dataset.label) || dataset.label;
    }

    if (capitalized) {
      formattedLabel = capitalize(formattedLabel);
    }

    return formattedLabel;
  });
};
